import React from 'react';
import {
  Typography,
  Container,
  Paper,
  Divider,
  Button,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { SEOHead } from '../../components/SEO';

const TermsContent = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  '& h2': {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  '& h3': {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    color: theme.palette.secondary.main,
  },
  '& p': {
    marginBottom: theme.spacing(1),
    lineHeight: 1.6,
  },
  '& ul': {
    marginLeft: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
}));

const TermsOfService: React.FC = () => {
  const navigate = useNavigate();
  const [termsVersion] = React.useState('1.0.0');

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#fafafa',
      paddingTop: 32,
      paddingBottom: 32,
    }}>
      <SEOHead
        title="Terms of Service"
        description="Read the Terms of Service for Prismatic Forge, the adaptive productivity system with AI teammates and knowledge graph technology."
        canonical="https://prismaticforge.ai/terms/"
        ogUrl="https://prismaticforge.ai/terms/"
      />
      <Container maxWidth="md">
        <div style={{ marginBottom: 24 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            style={{ marginBottom: 16 }}
          >
            Back to Home
          </Button>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            style={{ fontWeight: 'bold', color: '#1976d2' }}
          >
            Terms of Service
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Prismatic Forge Adaptive Productivity System
          </Typography>
        </div>

        <TermsContent elevation={3}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Version {termsVersion} - Effective Date: January 1, 2025
          </Typography>

          <Typography variant="h5" style={{ marginTop: 24, color: '#1976d2' }}>
            1. Acceptance of Terms
          </Typography>
          <Typography variant="body1" paragraph>
            By accessing or using the Prismatic Forge Adaptive Productivity System ("Service"),
            you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree
            to these Terms, you may not use the Service.
          </Typography>

          <Typography variant="h5" style={{ color: '#1976d2' }}>
            2. Service Description
          </Typography>
          <Typography variant="body1" paragraph>
            Prismatic Forge provides an adaptive productivity system including knowledge management,
            AI-powered assistants, collaboration tools, and professional engineering tools. The Service
            helps users organize, analyze, and collaborate on information through semantic knowledge
            graphs and intelligent automation.
          </Typography>

          <Typography variant="h5" style={{ color: '#1976d2' }}>
            3. User Accounts
          </Typography>
          <Typography variant="body1" paragraph>
            To use the Service, you must create an account and provide accurate, complete information.
            You are responsible for maintaining the security of your account credentials and for all
            activities that occur under your account. You must notify us immediately of any unauthorized
            use of your account.
          </Typography>

          <Typography variant="h5" style={{ color: '#1976d2' }}>
            4. Subscription and Billing
          </Typography>
          <Typography variant="body1" component="div">
            <Typography variant="h6" style={{ marginBottom: 8, color: '#9c27b0' }}>
              Subscription Tiers:
            </Typography>
            <ul>
              <li><strong>Starter:</strong> $29/month per user - Email integrations and core AI teammates</li>
              <li><strong>Professional:</strong> $59/month per user - Full Microsoft Graph and Google Workspace integration</li>
              <li><strong>Enterprise:</strong> $89/month per user - Advanced analytics and priority support</li>
              <li><strong>Industry-Specific:</strong> $129/month per user - Specialized tools including MEP Toolkit</li>
            </ul>
            <Typography variant="body1" paragraph style={{ marginTop: 16 }}>
              All subscription fees are billed monthly in advance and are non-refundable except as
              required by applicable law. You may cancel your subscription at any time, but cancellation
              will be effective at the end of your current billing period.
            </Typography>
          </Typography>

          <Typography variant="h5" style={{ color: '#1976d2' }}>
            5. User Content and Data
          </Typography>
          <Typography variant="body1" paragraph>
            You retain all ownership rights to content you submit, upload, or create using the Service
            ("User Content"). By using the Service, you grant us a limited, non-exclusive, royalty-free
            license to use, store, and process your User Content solely to provide the Service to you.
          </Typography>

          <Typography variant="h5" style={{ color: '#1976d2' }}>
            6. AI Services and Professional Tools
          </Typography>
          <Typography variant="body1" paragraph>
            Our AI agents and professional engineering tools (including the MEP Toolkit) are provided
            for assistance purposes only. While we strive for accuracy, AI-generated content and
            calculations should be reviewed and validated by qualified professionals. You are solely
            responsible for verifying the accuracy and appropriateness of any AI-generated content or
            calculations for your specific use case.
          </Typography>

          <Typography variant="h5" style={{ color: '#1976d2' }}>
            7. Acceptable Use
          </Typography>
          <Typography variant="body1" paragraph>
            You agree not to use the Service to:
          </Typography>
          <ul>
            <li>Violate any applicable laws or regulations</li>
            <li>Infringe on intellectual property rights</li>
            <li>Transmit harmful, threatening, or inappropriate content</li>
            <li>Attempt to gain unauthorized access to the Service or other users' accounts</li>
            <li>Interfere with or disrupt the Service's operation</li>
            <li>Use the Service for competitive analysis or to build competing products</li>
          </ul>

          <Typography variant="h5" style={{ color: '#1976d2' }}>
            8. Privacy and Data Protection
          </Typography>
          <Typography variant="body1" paragraph>
            We process your personal data in accordance with our Privacy Policy and comply with
            applicable data protection laws including GDPR, CCPA, and other regional privacy regulations.
            We implement industry-standard security measures to protect your data.
          </Typography>

          <Typography variant="h5" style={{ color: '#1976d2' }}>
            9. Intellectual Property
          </Typography>
          <Typography variant="body1" paragraph>
            The Service, including its software, algorithms, design, and documentation, is owned by
            Prismatic Forge and protected by intellectual property laws. You may not copy, modify,
            distribute, or reverse engineer any part of the Service without our written permission.
          </Typography>

          <Typography variant="h5" style={{ color: '#1976d2' }}>
            10. Limitation of Liability
          </Typography>
          <Typography variant="body1" paragraph>
            THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES AND ARE NOT LIABLE
            FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM
            YOUR USE OF THE SERVICE.
          </Typography>

          <Typography variant="h5" style={{ color: '#1976d2' }}>
            11. Termination
          </Typography>
          <Typography variant="body1" paragraph>
            Either party may terminate your account at any time. Upon termination, your access to
            the Service will cease, but you may export your data within 30 days. We may retain
            certain information as required by law or for legitimate business purposes.
          </Typography>

          <Typography variant="h5" style={{ color: '#1976d2' }}>
            12. Changes to Terms
          </Typography>
          <Typography variant="body1" paragraph>
            We may update these Terms from time to time. We will notify you of material changes
            by email or through the Service. Your continued use of the Service after such notification
            constitutes acceptance of the updated Terms.
          </Typography>

          <Typography variant="h5" style={{ color: '#1976d2' }}>
            13. Governing Law and Disputes
          </Typography>
          <Typography variant="body1" paragraph>
            These Terms are governed by the laws of Delaware, USA, without regard to conflict of
            law principles. Any disputes arising from these Terms or your use of the Service will
            be resolved through binding arbitration in accordance with the rules of the American
            Arbitration Association.
          </Typography>

          <Typography variant="h5" style={{ color: '#1976d2' }}>
            14. Contact Information
          </Typography>
          <Typography variant="body1" paragraph>
            For questions about these Terms, please contact us:
          </Typography>
          <Typography variant="body1" component="div">
            <strong>Legal:</strong> legal@prismaticforge.ai<br />
            <strong>Support:</strong> support@prismaticforge.ai<br />
            <strong>Address:</strong> Prismatic Forge, Inc.<br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;100 Innovation Drive<br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;San Francisco, CA 94105
          </Typography>

          <Divider style={{ marginTop: 32, marginBottom: 32 }} />

          <Typography variant="body2" color="text.secondary" style={{ textAlign: 'center' }}>
            Last updated: January 1, 2025
          </Typography>
        </TermsContent>
      </Container>
    </div>
  );
};

export default TermsOfService;